import { Col, Form, Input, Label, Row } from 'reactstrap';
import React, { useEffect, useMemo, useState } from 'react';

import Select from 'react-select';
import * as Yup from "yup"
import { useFormik } from "formik"
import { MODE, PRODUCT_TYPE_IDS } from 'helpers/sf_constants';
import { LEAD_INTEGRATION_PHASE } from 'pages/integrations/integration-components/connector/Connector';
import { getOptionFromValueForSelectBox, getPrimaryButtonClass, optionsNum1ToNum2 } from 'helpers/sf_utils';
import { useHistory } from 'react-router-dom';
import LabelWithTooltip from 'components/Common/LabelWithTooltip';
import AdminOnlyIcon from 'components/Common/AdminOnlyIcon';
import { useDispatch, useSelector } from 'react-redux';
import { getIntermediateLeadIntegrations, getResponseParserFunctions, updateIntermediateLeadIntegrations } from 'store/integration/actions';
import ReactMultiselectCheckboxes from 'react-multiselect-checkboxes/lib/ReactMultiselectCheckboxes';
import useIsLeadIntegrationsV2Enabled from 'hooks/useIsLeadIntegrationsV2Enabled';

// The only parser that reads the access path (ResponseParsers::PATH_AWARE_FN_NAMES).
const DYNAMIC_AUTH_PARSER = 'dynamic_auth';

const TOOLTIP_CONSTANTS = {
  ACCEPT_REJECT: {
    ID: "admin_settings_tooltip_posting_url",
    NAME: "Accept/Reject",
    TOOLTIP_MESSAGE: "If true, account is charged for leads only if the data post is successful",
    DESCRIPTION: "If true, account is charged for leads only if the data post is successful",
  },
  CREATE_TEMPLATE: {
    ID: "admin_settings_tooltip_create_template",
    NAME: "Create Template",
    TOOLTIP_MESSAGE: "Save this setup as a new template that can be used later",
    DESCRIPTION: "Creates a template for other accounts to use",
  },
  PRIORITY: {
    ID: "admin_settings_tooltip_priority",
    NAME: "Priority",
    TOOLTIP_MESSAGE: "Accept/Reject integrations are attempted one at a time with the chain halting at the first failure or after all integrations are attempted. Priority setting decides which integration is executed first. Lower number denotes high priority.",
    DESCRIPTION: "Accept/Reject integrations are attempted one at a time with the chain halting at the first failure or after all integrations are attempted. Priority setting decides which integration is executed first. Lower number denotes high priority.",
  },
  IS_INTERMEDIATE: {
    ID: "admin_settings_tooltip_is_intermediate",
    NAME: "Intermediate Integration Enabled",
    TOOLTIP_MESSAGE: "Intermediate Integration",
    DESCRIPTION: "Enable/Disable Intermediate integration",
  },
  ATTACH_TO: {
    ID: "admin_settings_tooltip_attach_to",
    NAME: "Attach To",
    TOOLTIP_MESSAGE: "Attach to other integrations",
    DESCRIPTION: "Attach to other integrations",
  },
  RESPONSE_PARSER_FUNCTIONS: {
    ID: "admin_settings_tooltip_response_parser_functions",
    NAME: "Response parser functions",
    TOOLTIP_MESSAGE: "Response parser functions",
    DESCRIPTION: "Response parser functions",
  },
  RESPONSE_PARSER_ACCESS_PATH: {
    ID: "admin_settings_tooltip_response_parser_access_path",
    NAME: "Response Parser Access Path",
    TOOLTIP_MESSAGE: "Where dynamic_auth reads the token in the intermediate response. Body path (bearerToken, data.access_token, data.tokens.0.value) or a header (header:EZToken). Only dynamic_auth uses it.",
    DESCRIPTION: "Where dynamic_auth reads the token in the intermediate response.",
  },
  TIMEOUT: {
    ID: "admin_settings_tooltip_timeout",
    NAME: "Timeout",
    TOOLTIP_MESSAGE: "Number of seconds after which we timeout the connection. We treat all timeouts as failure.",
    DESCRIPTION: "Number of seconds after which we timeout the connection. We treat all timeouts as failure."
  },
}

function AdminSettings({ integration_id, mode, initialState, onSubmitState, openNextAccordion }) {

  const options1to10 = optionsNum1ToNum2(1, 10)

  const timeoutOptions = useMemo(() => {
    const isPingClickPing =
      initialState?.lead_integration?.is_ping === true &&
      initialState?.lead_integration?.product_type_id === PRODUCT_TYPE_IDS.CLICKS &&
      initialState?.lead_integration?.phase === LEAD_INTEGRATION_PHASE.PING
    const options1to10Seconds = optionsNum1ToNum2(1, 10).map((option) => ({ ...option, label: `${option.label} seconds` }))
    const options1to200 = optionsNum1ToNum2(1, 200).map((option) => ({ ...option, label: `${option.label} seconds` }))
    return isPingClickPing ? options1to10Seconds : options1to200
  }, [
    initialState?.lead_integration?.is_ping,
    initialState?.lead_integration?.product_type_id,
    initialState?.lead_integration?.phase,
  ])

  const history = useHistory();
  const dispatch = useDispatch();
  const isV2IntegrationsEnabled = useIsLeadIntegrationsV2Enabled();

  const { response_parser_functions, integrations, intermediate_integrations, v2_account_integrations } = useSelector((state) => ({
    response_parser_functions: state.Integration.response_parser_functions,
    integrations: state.Integration.integrations,
    intermediate_integrations: state.Integration.intermediate_integrations,
    v2_account_integrations: state.IntegrationV2.account_integrations,
  }))
  const allIntegrations = isV2IntegrationsEnabled ? v2_account_integrations : integrations;

  const [selectedLeadIntegration, setSelectedLeadIntegration] = useState([]);

  useEffect(() => {
    dispatch(getResponseParserFunctions())
    dispatch(getIntermediateLeadIntegrations(integration_id))
  }, [])

  const responseParserFunctionsOptions = useMemo(() => {
    const options = [];

    response_parser_functions?.map((func) => {
      options.push({
        label: func?.fn_name,
        value: func?.id
      })
    })

    return options
  }, [response_parser_functions])

  const leadIntegrationsOptions = useMemo(() => {
    const IntegrationOpts = [{ options: [] }];
    IntegrationOpts[0].options = [];
    IntegrationOpts[0].options = IntegrationOpts[0].options.concat(allIntegrations?.filter((ele) => ele?.id != integration_id && ele?.is_template !== true))?.map((element) => ({ label: element.name, value: element.id }))

    return IntegrationOpts
  }, [allIntegrations, integration_id])

  useEffect(() => {
    if (mode == MODE.EDIT) {
      //getting ids of the lead integrations which are attached to the integration we are editing
      const lead_integration_ids = intermediate_integrations?.map((imd_int) => {
        return imd_int?.lead_integration_id
      })
      const selected_lead_integrations = lead_integration_ids?.map((lead_integration_id) => {
        const lead_int = allIntegrations?.find((ele) => ele?.id == lead_integration_id)
        return {
          label: lead_int?.name,
          value: lead_int?.id
        }
      })

      setSelectedLeadIntegration(selected_lead_integrations)
    }
  }, [intermediate_integrations, integration_id, allIntegrations, leadIntegrationsOptions])

  function handleSelectedLeadIntegration(selectedOption, event) {
    setSelectedLeadIntegration(selectedOption);
  }

  const maxTimeoutSeconds = timeoutOptions?.length ? 10 : 200
  const initialTimeoutValue = (initialState?.lead_integration?.req_timeout > maxTimeoutSeconds)
    ? maxTimeoutSeconds
    : initialState?.lead_integration?.req_timeout

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      accept_reject: initialState?.lead_integration?.accept_reject,
      is_intermediate: initialState?.lead_integration?.is_intermediate,
      response_parser_function: getOptionFromValueForSelectBox(responseParserFunctionsOptions, initialState?.lead_integration?.response_parser_function_id),
      response_parser_access_path: initialState?.lead_integration?.response_parser_access_path || '',
      has_created_template: initialState?.lead_integration?.has_created_template || false,
      priority: getOptionFromValueForSelectBox(options1to10, initialState?.lead_integration?.priority),
      req_timeout: getOptionFromValueForSelectBox(timeoutOptions, initialTimeoutValue),
      ping_pii: initialState?.lead_integration?.ping_pii || false,
    },
    validationSchema: Yup.object({
    }),
    onSubmit: values => {
      const lead_integration = {
        accept_reject: values?.accept_reject,
        priority: values?.priority?.value,
        req_timeout: values?.req_timeout?.value,
        has_created_template: values?.has_created_template,
        ping_pii: values?.ping_pii,
        is_intermediate: values?.is_intermediate,
        lead_integration_ids: selectedLeadIntegration?.map((ele) => ele?.value),
        response_parser_function_id: values?.response_parser_function?.value,
        response_parser_access_path: values?.response_parser_access_path
      }

      if (mode === MODE.EDIT) {
        onSubmitState({ lead_integration })
        dispatch(updateIntermediateLeadIntegrations({ intermediate_integration_id: integration_id, lead_integration_ids: lead_integration?.lead_integration_ids }))
        history.push("/integrations")
      }
      if (mode === MODE.CREATE) {
        dispatch(updateIntermediateLeadIntegrations({ intermediate_integration_id: integration_id, lead_integration_ids: lead_integration?.lead_integration_ids }))
        onSubmitState({ ...initialState, lead_integration: { ...initialState?.lead_integration, ...lead_integration } });
      }
    },
  });

  function getDropdownButtonLabelForLeadIntegrations({ placeholderButtonLabel, value }) {

    //removing invalid options if there are any
    const filteredValues = value?.filter((val) => val?.value != undefined || null)
    return <span className='css-1v99tuv text-black align-self-center'>{filteredValues?.length} selected</span>;
  }

  return (
    <React.Fragment>
      <Form
        className="align-items-center mt-2"
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit();
          return false;
        }}
      >
        <Row>
          <Row className='d-flex'>
            <Col lg="6">
              {/* Accept Reject */}
              <Col lg='12' className='d-flex align-items-top pt-3'>
                <div className="d-flex form-check form-switch form-switch-md">
                  <Col sm='auto'>
                    <input
                      name="accept_reject"
                      id="accept_reject"
                      type="checkbox"
                      className="form-check-input"
                      value={validation.values.accept_reject}
                      checked={validation.values.accept_reject}
                      onChange={validation.handleChange}
                    />
                  </Col>
                  <Col className='ms-2'>
                    <LabelWithTooltip target={TOOLTIP_CONSTANTS.ACCEPT_REJECT.ID} name={TOOLTIP_CONSTANTS.ACCEPT_REJECT.NAME} tooltip_message={TOOLTIP_CONSTANTS.ACCEPT_REJECT.TOOLTIP_MESSAGE} htmlFor="accept_reject" isAdminSettingIcon={true} />
                    <span>{TOOLTIP_CONSTANTS.ACCEPT_REJECT.DESCRIPTION}</span>
                  </Col>
                </div>
              </Col>
              {/* Accept Reject */}

              {/* Create Template */}
              {!initialState?.lead_integration?.is_template && (
                <Col lg='12' className='d-flex align-items-top pt-3'>
                  <div className="d-flex form-check form-switch form-switch-md">
                    <Col sm='auto'>
                      <input
                        name="has_created_template"
                        id="has_created_template"
                        disabled={initialState?.lead_integration?.has_created_template || false}
                        type="checkbox"
                        className="form-check-input"
                        value={validation.values.has_created_template}
                        checked={validation.values.has_created_template}
                        onChange={validation.handleChange}
                      />
                    </Col>
                    <Col className='ms-2'>
                      <LabelWithTooltip target={TOOLTIP_CONSTANTS.CREATE_TEMPLATE.ID} name={TOOLTIP_CONSTANTS.CREATE_TEMPLATE.NAME} tooltip_message={TOOLTIP_CONSTANTS.CREATE_TEMPLATE.TOOLTIP_MESSAGE} htmlFor="has_created_template" isAdminSettingIcon={true} />
                      <span>{TOOLTIP_CONSTANTS.CREATE_TEMPLATE.DESCRIPTION}</span>
                    </Col>
                  </div>
                </Col>
              )}
              <Col md={12} xl={12}>
                <div className="d-flex form-check form-switch form-switch-md mt-3">
                  <div>
                    <input
                      name="ping_pii"
                      disabled={!initialState?.lead_integration?.is_ping}
                      id="integration_is_ping_ping_pii"
                      type="checkbox"
                      className="form-check-input"
                      value={validation.values?.ping_pii}
                      checked={validation.values?.ping_pii}
                      onChange={validation.handleChange}
                    />
                  </div>
                  <div className='ms-2'>
                    <Label className='font-weight-semibold mb-0' htmlFor='integration_is_ping_ping_pii'>Ping PII data <small className='text-warning'>{!initialState?.lead_integration?.is_ping && "(This option is only available for ping integrations.)"}</small>
                      <AdminOnlyIcon idFor="pii_data" />
                    </Label>
                    <p>
                      We will send Personally Identifiable Information (PII) data in ping request
                    </p>
                  </div>
                </div>
              </Col>
              {/* Create Template */}
              {/* Priority */}
              {validation.values.accept_reject && (
                <Col lg="12">
                  <div className="form-group mb-3 pt-3">
                    <LabelWithTooltip target={TOOLTIP_CONSTANTS.PRIORITY.ID} name={TOOLTIP_CONSTANTS.PRIORITY.NAME} tooltip_message={TOOLTIP_CONSTANTS.PRIORITY.TOOLTIP_MESSAGE} htmlFor="integration_priority" isAdminSettingIcon={true} />
                    <Select
                      options={options1to10}
                      name="priority"
                      menuPlacement="top"
                      id="integration_priority"
                      classNamePrefix="select2-selection"
                      value={validation.values.priority}
                      onChange={selectedOption => {
                        let event = {
                          target: {
                            name: 'priority',
                            value: selectedOption,
                          },
                        }
                        validation.handleChange(event)
                      }}
                      onBlur={() => {
                        validation.handleBlur({
                          target: {
                            name: 'priority',
                          },
                        })
                      }}
                    />
                  </div>
                </Col>
              )}
              <Col lg='12' className='d-flex align-items-top pt-3'>
                <div className="d-flex form-check form-switch form-switch-md">
                  <Col sm='auto'>
                    <input
                      name="is_intermediate"
                      id="is_intermediate"
                      type="checkbox"
                      className="form-check-input"
                      value={validation.values.is_intermediate}
                      checked={validation.values.is_intermediate}
                      onChange={validation.handleChange}
                    />
                  </Col>
                  <Col className='ms-2'>
                    <LabelWithTooltip target={TOOLTIP_CONSTANTS.IS_INTERMEDIATE.ID} name={TOOLTIP_CONSTANTS.IS_INTERMEDIATE.NAME} tooltip_message={TOOLTIP_CONSTANTS.IS_INTERMEDIATE.TOOLTIP_MESSAGE} htmlFor="is_intermediate" isAdminSettingIcon={true} is_active={false} />
                    <span>{TOOLTIP_CONSTANTS.IS_INTERMEDIATE.DESCRIPTION}</span>
                  </Col>
                </div>
              </Col>
              {validation.values.is_intermediate && (
                <Row>
                  <Col lg="6">
                    <div className="form-group mb-3 pt-3">
                      <LabelWithTooltip target={TOOLTIP_CONSTANTS.ATTACH_TO.ID} name={TOOLTIP_CONSTANTS.ATTACH_TO.NAME} tooltip_message={TOOLTIP_CONSTANTS.ATTACH_TO.TOOLTIP_MESSAGE} htmlFor="attach_to" isAdminSettingIcon={true} is_active={false} />

                      <ReactMultiselectCheckboxes
                        className="sf-checkbox"
                        hideSearch
                        getDropdownButtonLabel={getDropdownButtonLabelForLeadIntegrations}
                        value={selectedLeadIntegration}
                        onChange={(selectedOption, event) => {
                          handleSelectedLeadIntegration(selectedOption, event);
                        }}
                        options={leadIntegrationsOptions[0].options}
                      />
                    </div>
                  </Col>
                  <Col lg="6">
                    <div className="form-group mb-3 pt-3">
                      <LabelWithTooltip target={TOOLTIP_CONSTANTS.RESPONSE_PARSER_FUNCTIONS.ID} name={TOOLTIP_CONSTANTS.RESPONSE_PARSER_FUNCTIONS.NAME} tooltip_message={TOOLTIP_CONSTANTS.RESPONSE_PARSER_FUNCTIONS.TOOLTIP_MESSAGE} htmlFor="response_parser_functions" isAdminSettingIcon={true} is_active={false} />
                      <Select
                        options={responseParserFunctionsOptions}
                        name="response_parser_function"
                        id="response_parser_function"
                        menuPlacement="top"
                        classNamePrefix="select2-selection"
                        value={validation.values.response_parser_function}
                        onChange={selectedOption => {
                          let event = {
                            target: {
                              name: 'response_parser_function',
                              value: selectedOption,
                            },
                          }
                          validation.handleChange(event)
                        }}
                        onBlur={() => {
                          validation.handleBlur({
                            target: {
                              name: 'response_parser_function',
                            },
                          })
                        }}
                      />
                    </div>
                  </Col>
                  {validation.values.response_parser_function?.label === DYNAMIC_AUTH_PARSER && (
                  <Col lg="6">
                    <div className="form-group mb-3 pt-3">
                      <LabelWithTooltip target={TOOLTIP_CONSTANTS.RESPONSE_PARSER_ACCESS_PATH.ID} name={TOOLTIP_CONSTANTS.RESPONSE_PARSER_ACCESS_PATH.NAME} tooltip_message={TOOLTIP_CONSTANTS.RESPONSE_PARSER_ACCESS_PATH.TOOLTIP_MESSAGE} htmlFor="response_parser_access_path" isAdminSettingIcon={true} is_active={false} />
                      <Input
                        type="text"
                        name="response_parser_access_path"
                        id="response_parser_access_path"
                        className="form-control"
                        placeholder="data.access_token"
                        value={validation.values.response_parser_access_path || ''}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </Col>
                  )}
                </Row>

              )}


              {/* Priority */}
              {/* Timeout */}
              <Col lg="12">
                <div className="form-group mb-3 pt-3">
                  <LabelWithTooltip target={TOOLTIP_CONSTANTS.TIMEOUT.ID} name={TOOLTIP_CONSTANTS.TIMEOUT.NAME} tooltip_message={TOOLTIP_CONSTANTS.TIMEOUT.TOOLTIP_MESSAGE} htmlFor="integration_req_timeout" isAdminSettingIcon={true} />
                  <Select
                    options={timeoutOptions}
                    name="req_timeout"
                    id="integration_req_timeout"
                    menuPlacement="top"
                    classNamePrefix="select2-selection"
                    value={validation.values.req_timeout}
                    onChange={selectedOption => {
                      let event = {
                        target: {
                          name: 'req_timeout',
                          value: selectedOption,
                        },
                      }
                      validation.handleChange(event)
                    }}
                    onBlur={() => {
                      validation.handleBlur({
                        target: {
                          name: 'req_timeout',
                        },
                      })
                    }}
                  />
                </div>
              </Col>
              {/* Timeout */}
            </Col>
          </Row>
          <Row>
            <Col lg="12" md="12">
              <div className="form-group align-self-end mb-3 pt-3">
                <button className={`btn ${getPrimaryButtonClass()} flex-item`} type="submit" name="submit"> {false ? "Saving" : (mode === MODE.EDIT ? "Save" : "Create")}</button>
                <button hidden={false ? false : true} className="btn btn-light ms-3 flex-item" type="button" onClick={validation.resetForm} name="cancel">Cancel</button>
              </div>
            </Col>
          </Row>
        </Row>
      </Form>
    </React.Fragment>
  )
}
export default AdminSettings;